<template>
  <div class="countdown-card">
    <div class="countdown-card__table">
      <div
        :key="intervalCount"
        :class="[
          'countdown-card__cards',
          { 'countdown-card__cards--flip': animationActive },
        ]"
      >
        <div class="countdown-card__card">
          <div class="countdown-card__card-top">{{ intervalCount }}</div>
          <div
            class="countdown-card__card-bottom"
            :data-i="intervalCount + 1"
          />
          <div class="countdown-card__card-back" :data-i="intervalCount + 1">
            <div class="countdown-card__card-bottom" :data-i="intervalCount" />
          </div>
        </div>
      </div>
      <div class="countdown-card__line-bg" />
      <div class="countdown-card__line" />
    </div>
    <div v-if="$slots.text" class="countdown-card__text">
      <slot name="text" />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";

export default {
  name: "CountdownCard",
  props: {
    count: {
      type: Number,
      default: 0,
    },
    animateCount: {
      type: Number,
      default: 3,
    },
    animationDelay: {
      type: Number,
      default: 500,
    },
  },
  setup(props) {
    const timeout = ref(null);
    const interval = ref(null);
    const intervalCount = ref(0);
    const animationActive = ref(false);

    const initCount = () => {
      intervalCount.value = props.count + props.animateCount;
    };

    const startAnimation = () => {
      timeout.value = setTimeout(() => {
        interval.value = setInterval(() => {
          if (intervalCount.value === props.count) {
            return clearInterval(interval.value);
          }
          animationActive.value = true;
          intervalCount.value -= 1;
        }, 300);
      }, props.animationDelay);
    };

    onMounted(() => {
      initCount();
      startAnimation();
    });

    onUnmounted(() => {
      clearTimeout(timeout.value);
      clearInterval(interval.value);
    });

    return {
      intervalCount,
      animationActive,
    };
  },
};
</script>

<style lang="scss" scoped>
.countdown-card {
  position: relative;
  width: 84px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.15;

  &__table {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 68px;
    margin: 0 auto;
    text-align: center;

    *,
    *:before,
    *:after {
      box-sizing: border-box;
    }

    @include lg {
      height: 51px;
    }
  }

  &__line-bg,
  &__line {
    position: absolute;
    top: 33px;
    z-index: 1;
    width: 100%;
    height: 2px;

    &:before,
    &:after {
      content: "";
      position: absolute;
      top: 50%;
      width: 2px;
      height: 12px;
      background-color: inherit;
      border-radius: 10px;
      transform: translateY(-50%);
    }

    &:before {
      left: 6px;
    }

    &:after {
      right: 6px;
    }

    @include lg {
      top: 25px;

      &:before,
      &:after {
        height: 10px;
      }
    }
  }

  &__line {
    &:before,
    &:after {
      background-color: #413228;
    }
  }

  &__text {
    margin-top: 5px;
    font-weight: 500;
    line-height: 100%;
    text-align: center;
    opacity: 0.6;
  }

  &__cards {
    position: relative;
    z-index: 1;
    display: inline-block;
    width: 100%;
    height: 100%;
    margin: 0 auto;

    &--flip {
      .countdown-card__card-back {
        &:before {
          transform-origin: center bottom;
          animation: flip-top 0.3s cubic-bezier(0.37, 0.01, 0.94, 0.35) both;
        }

        .countdown-card__card-bottom {
          transform-origin: center top;
          animation: flip-bottom 0.4s cubic-bezier(0.15, 0.45, 0.28, 1) both;
        }
      }
    }
  }

  &__card {
    position: relative;
    display: block;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding-bottom: 34px;
    font-weight: 700;
    font-size: 58px;
    color: #ff0020;
    border-radius: 5px;

    &:not(:last-child) {
      margin-bottom: 2px;
    }

    @include lg {
      padding-bottom: 25px;
      font-size: 44px;
    }
  }

  &__card-top {
    overflow: hidden;
  }

  &__card-top,
  &__card-bottom,
  &__card-back:before,
  &__card-back:after {
    display: block;
    width: 100%;
    height: 33px;
    padding-top: 0;
    background-color: #e0e0e0;
    border-radius: 5px;
    transform: translateZ(0);
    transform-style: preserve-3d;
    backface-visibility: hidden;

    @include lg {
      height: 25px;
    }
  }

  &__card-bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    overflow: hidden;
    background-color: #fff;
    border-top: none;

    &:after {
      display: block;
      margin-top: -33px;

      @include lg {
        margin-top: -25px;
      }
    }
  }

  &__card-back {
    position: absolute;
    top: 0;
    left: 0%;
    width: 100%;
    height: 100%;

    &:before {
      position: relative;
      z-index: -1;
      overflow: hidden;
    }

    &:before,
    .countdown-card__card-bottom:after {
      content: attr(data-i);
    }
  }

  @keyframes flip-top {
    0% {
      z-index: 2;
      transform: rotateX(0deg);
      opacity: 1;
    }

    99% {
      opacity: 1;
    }

    100% {
      transform: rotateX(-180deg);
      opacity: 0;
    }
  }

  @keyframes flip-bottom {
    0% {
      z-index: -1;
      transform: rotateX(180deg);
      opacity: 0;
    }

    50% {
      z-index: -1;
      transform: rotateX(180deg);
      opacity: 0;
    }

    51% {
      opacity: 0.99;
    }

    100% {
      z-index: 5;
      transform: rotateX(0deg);
      opacity: 0.99;
    }
  }

  @include lg {
    width: 64px;

    .countdown-card__card {
      line-height: 1.36;
    }
  }
}
</style>
